import { type DragEvent, useState } from 'react';

type UseImageAttachmentDropParams = {
  disabled: boolean;
  onError: (message: string) => void;
  onFileSelect: (file: File) => void;
  validateFile: (file: File) => string | undefined;
};

type UseImageAttachmentDropResult = {
  handleDragLeave: (event: DragEvent<HTMLButtonElement>) => void;
  handleDragOver: (event: DragEvent<HTMLButtonElement>) => void;
  handleDrop: (event: DragEvent<HTMLButtonElement>) => void;
  isDragOver: boolean;
};

export const useImageAttachmentDrop = (params: UseImageAttachmentDropParams): UseImageAttachmentDropResult => {
  const { disabled, onError, onFileSelect, validateFile } = params;
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>): void => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>): void => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>): void => {
    if (disabled) {
      return;
    }

    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];

    if (!file) {
      return;
    }

    const validationError = validateFile(file);

    if (validationError) {
      onError(validationError);
      return;
    }

    onFileSelect(file);
  };

  return {
    handleDragLeave,
    handleDragOver,
    handleDrop,
    isDragOver,
  };
};
